import React, { FC, useEffect, useCallback } from 'react';

import { StartRenderType } from 'rcx_adapter';
import i18n from 'rcx_i18n';

import { Loading } from './Loading';
import { StyledSecondIFrameContainer } from './RCX.styled';
import { SecondIFrame } from './SecondIFrame';
import { SubmittedSplash } from './SubmittedSplash';
import { ToggleButton } from './ToggleButton';
import { IFRAME_TOGGLE_WIDTH, SECOND_IFRAME_WIDTH, SecondIframeState } from '../constants';
import { SecondIframeAdapter } from '../services/SecondIframeAdapter';
import { changeSecondIframeState, setSecondIframeScript } from '../state/actions';
import { useSecondIFrameDispatch, useSecondIFrameState } from '../state/StateContext';

export const SecondIFrameContainer: FC<{ secondIframeAdapter: SecondIframeAdapter }> = ({ secondIframeAdapter }) => {
    const {
        secondIframe: { state: secondIframeState, currentType, scriptUrl, script, digital, isLoading },
    } = useSecondIFrameState();
    const dispatch = useSecondIFrameDispatch();
    const visible = secondIframeState !== SecondIframeState.Hidden;
    const isCollapse = secondIframeState === SecondIframeState.Collapse;

    const showScript = currentType === StartRenderType.SCRIPT && !script?.submitted;
    const showDigital = currentType === StartRenderType.DIGITAL && !!digital?.url;
    const showScriptSubmitted = currentType === StartRenderType.SCRIPT && script?.submitted && !script?.isEnd;

    const handleToggleSecondIFrame = () => {
        secondIframeAdapter.analyticsTrack('RCX_collapse_2ndiframe', { type: currentType });
        dispatch(changeSecondIframeState(isCollapse ? SecondIframeState.Expand : SecondIframeState.Collapse));
    };

    const handleSecondIframeLoaded = (scriptIframe: HTMLIFrameElement) => {
        secondIframeAdapter.setScriptWindow(scriptIframe);
        secondIframeAdapter.addMessageListener();
    };

    useEffect(() => {
        if (script?.isEnd && script?.submitted) {
            dispatch(setSecondIframeScript(null));
        }
    }, [script]);

    useEffect(() => {
        if (script?.scriptId) {
            secondIframeAdapter.rerenderScript(script.scriptId);
        }
    }, [script?.scriptId, script?.renderId]);

    const renderFrames = useCallback(
        () => (
            <>
                {showScriptSubmitted && (
                    <SubmittedSplash
                        title={i18n.t('secondIframe.dispositionSubmittedMsg')}
                        desc={i18n.t('secondIframe.dispositionSubmittedSubMsg')}
                    />
                )}
                {scriptUrl && (
                    <SecondIFrame
                        visible={showScript}
                        url={scriptUrl}
                        onLoaded={handleSecondIframeLoaded}
                        id='voice-ringcx-second-frame'
                    />
                )}
                {digital?.url && (
                    <SecondIFrame visible={showDigital} url={digital.url} id='digital-ringcx-second-frame' />
                )}
            </>
        ),
        [showScript, showDigital, showScriptSubmitted, scriptUrl, script?.renderId, digital]
    );
    const geIFrameWidth = () => {
        if (!visible || isCollapse) {
            return '0px';
        } else if (secondIframeAdapter.getCTIType?.() === 'SF_UNDOCK') {
            return '100%';
        } else {
            return `${SECOND_IFRAME_WIDTH}px`;
        }
    };
    return (
        <>
            {visible && (
                <ToggleButton width={IFRAME_TOGGLE_WIDTH} isCollapse={isCollapse} onClick={handleToggleSecondIFrame} />
            )}
            <StyledSecondIFrameContainer width={geIFrameWidth()}>
                <Loading visible={isLoading} />
                {renderFrames()}
            </StyledSecondIFrameContainer>
        </>
    );
};
